import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, TextField, Paper, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";

import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";

import Menu from "./Menu";
import UploadFileIcon from "@mui/icons-material/UploadFile";

function Uploaddocuments() {

    const [userId, setUserId] = useState(null);
    const [propertyId, setPropertyId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);


    const [documentData, setDocumentData] = useState({});
    const [accountDetails, setAccountDetails] = useState({
        acc_no: "", acc_name: "", ifsc_code: "", branch: ""
    });

    const [message, setMessage] = useState("");

    // -------------------------------------------
    // Load logged-in user details
    // -------------------------------------------
    useEffect(() => {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) return;

        const uid = currentUser.uid;
        setUserId(uid);

        const loadData = async () => {
            const userRef = doc(db, "authroles", uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const user = userSnap.data();
                setPropertyId(user.propertyId);
            }

            // Load existing documents (if available)
            const documentRef = doc(db, "documents", `${uid}_${userSnap.data().propertyId}`);
            const docSnap = await getDoc(documentRef);

            if (docSnap.exists()) {
                setDocumentData(docSnap.data());

                if (docSnap.data().account_details) {
                    setAccountDetails(docSnap.data().account_details);
                }
            }

            setLoading(false);
        };
        loadData();
    }, []);

    // -------------------------------------------
    // Upload file
    // -------------------------------------------
    const uploadFile = async (file, key, folder) => {
        return new Promise((resolve, reject) => {
            const storageRef = ref(storage, `${folder}/${userId}_${propertyId}_${file.name}`);

            setIsUploading(true);
            setUploadProgress(0);

            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress.toFixed(0));
                },
                (error) => {
                    setIsUploading(false);
                    reject(error);
                },
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    setIsUploading(false);
                    resolve(url);
                }
            );
        });
    };


    // -------------------------------------------
    // Update Firestore
    // -------------------------------------------
    const updateDocumentStatus = async (key, url) => {
        const documentRef = doc(db, "documents", `${userId}_${propertyId}`);

        await setDoc(documentRef, {
            ...documentData,
            [key]: {
                url: url,
                status: "pending"    // Reset status after re-upload
            },
            account_details: accountDetails,
            userId,
            propertyId,
            status: "pending"
        }, { merge: true });

        setMessage("Document updated successfully!");

        setDocumentData(prev => ({
            ...prev,
            [key]: { url, status: "pending" }
        }));
    };

    // -------------------------------------------
    // Table Fields
    // -------------------------------------------
    const fields = [
        { key: "gst", label: "GST", folder: "business_doc" },
        { key: "msme", label: "MSME", folder: "business_doc" },
        { key: "shopact", label: "Shop Act", folder: "business_doc" },
        { key: "any_other", label: "Any Other", folder: "business_doc" },
        { key: "pan_tan", label: "PAN / TAN", folder: "address_proof" },
        { key: "bank_statement", label: "Bank Statement", folder: "bank_details" },
        { key: "cancelled_cheque", label: "Cancelled Cheque", folder: "bank_details" },
        { key: "passbook", label: "Passbook", folder: "bank_details" }
    ];

    // -------------------------------------------
    // Upload Handler
    // -------------------------------------------
    const handleUpload = async (e, field) => {
        const file = e.target.files[0];
        if (!file) return;

        const url = await uploadFile(file, field.key, field.folder);

        await updateDocumentStatus(field.key, url);
    };

    if (loading) {
        return (
            <>
                <Menu />
                <Typography sx={{ p: 4 }}>Loading...</Typography>
            </>
        );
    }

    return (
        <>
            <Menu />
            <div
                className="content-wrapper"
                style={{
                    backgroundColor: "#f5f5f5",
                    minHeight: "100vh",
                    padding: "30px 20px",
                }}
            >
                <h4 className="mb-4">Upload Documents</h4>
                {message && <Alert severity="success" sx={{ mb: 3 }}>{message}</Alert>}
                {isUploading && (
                    <Paper sx={{ p: 2, mb: 2, background: "#fff3cd" }} elevation={1}>
                        <Typography sx={{ mb: 1 }}><b>Uploading Document...</b></Typography>

                        <Box sx={{ width: "100%", background: "#eee", height: 10, borderRadius: 1 }}>
                            <Box
                                sx={{
                                    width: `${uploadProgress}%`,
                                    height: "100%",
                                    background: "#1976d2",
                                    borderRadius: 1,
                                    transition: "0.3s"
                                }}
                            ></Box>
                        </Box>

                        <Typography sx={{ mt: 1 }}>{uploadProgress}%</Typography>
                    </Paper>
                )}

                <Box className="row">


                    {/* -------------------- TABLE -------------------- */}
                    <div className="col-sm-6">
                        <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>Document Name</b></TableCell>
                                            <TableCell><b>Upload / Re-upload</b></TableCell>
                                            <TableCell><b>Status</b></TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {fields.map((f) => {
                                            const info = documentData[f.key];
                                            const status = info?.status || "pending";

                                            return (
                                                <TableRow key={f.key}>
                                                    {/* Name */}
                                                    <TableCell>{f.label}</TableCell>

                                                    {/* Upload Button */}
                                                    <TableCell>
                                                        {status === "approved" ? (
                                                            // If approved → show View button
                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                onClick={() => window.open(info?.url, "_blank")}
                                                            >
                                                                View
                                                            </Button>
                                                        ) : (
                                                            // If not approved → show upload button
                                                            <Button
                                                                variant="outlined"
                                                                component="label"
                                                                startIcon={<UploadFileIcon />}
                                                            >
                                                                {info?.url ? "Re-upload" : "Upload"}
                                                                <input
                                                                    type="file"
                                                                    hidden
                                                                    accept=".pdf,.jpg,.png"
                                                                    onChange={(e) => handleUpload(e, f)}
                                                                />
                                                            </Button>
                                                        )}
                                                    </TableCell>

                                                    {/* Status */}
                                                    <TableCell>
                                                        <Typography
                                                            color={
                                                                status === "approved"
                                                                    ? "green"
                                                                    : status === "rejected"
                                                                        ? "red"
                                                                        : "orange"
                                                            }
                                                        >
                                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </div>

                    {/* -------------------- BANK ACCOUNT -------------------- */}
                    <div className="col-sm-6">
                        <Paper sx={{ p: 3 }} elevation={3}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Account Information</Typography>

                            <TextField fullWidth size="small" label="Account Number"
                                sx={{ mb: 2 }}
                                value={accountDetails.acc_no}
                                onChange={(e) => setAccountDetails({ ...accountDetails, acc_no: e.target.value })}
                            />

                            <TextField fullWidth size="small" label="Account Holder Name"
                                sx={{ mb: 2 }}
                                value={accountDetails.acc_name}
                                onChange={(e) => setAccountDetails({ ...accountDetails, acc_name: e.target.value })}
                            />

                            <TextField fullWidth size="small" label="IFSC Code"
                                sx={{ mb: 2 }}
                                value={accountDetails.ifsc_code}
                                onChange={(e) => setAccountDetails({ ...accountDetails, ifsc_code: e.target.value })}
                            />

                            <TextField fullWidth size="small" label="Branch"
                                sx={{ mb: 2 }}
                                value={accountDetails.branch}
                                onChange={(e) => setAccountDetails({ ...accountDetails, branch: e.target.value })}
                            />

                            <Button
                                variant="contained"
                                onClick={async () => {
                                    await updateDocumentStatus("account_details", accountDetails);
                                    setMessage("Account details updated!");
                                }}
                            >
                                Save Account Details
                            </Button>
                        </Paper>
                    </div>

                </Box>



            </div>
        </>
    );
}

export default Uploaddocuments;
